import { useState } from "react";
import { Link } from "react-router-dom";
import { buildStarterPrompt } from "@/lib/starterPrompt";
import type { DocumentKind } from "@/lib/api";

/**
 * Empty-state "what now?" panel shown on the Document page when the user has
 * just set up the doc and no agent has done anything yet. Self-dismisses via
 * the parent component once SSE picks up an agent revision.
 *
 * Three pieces:
 *   1. The starter prompt the user pastes into their AI client (kind-aware).
 *   2. Quick links to open Claude.ai / Codex web / ChatGPT in a new tab.
 *   3. A reminder that Connectors / Sources are reachable from the doc.
 */
export function GetStartedPanel({
  docId,
  title,
  kind,
  language,
}: {
  docId: string;
  title: string;
  kind: DocumentKind;
  language?: string | null;
}) {
  const prompt = buildStarterPrompt({
    sessionId: docId,
    title,
    kind,
    language: language ?? null,
  });

  const [copied, setCopied] = useState(false);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* no-op — clipboard might be unavailable in some contexts */
    }
  };

  const copyAndOpen = (url: string) => async () => {
    await copyPrompt();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="text-2xl">🟢</div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold mb-1">Your agent hasn't started yet</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Paste this prompt into Claude.ai, Claude Code, ChatGPT, or
              Codex — the connector you set up will pick it up. The page will
              update live as your agent works.
            </p>
          </div>
        </div>

        {/* Prompt block */}
        <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 p-3 mb-3">
          <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-zinc-800 dark:text-zinc-200">
            {prompt}
          </pre>
        </div>

        {/* Action row */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={copyPrompt}
            className="text-sm rounded-md bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 font-medium hover:opacity-90 disabled:opacity-50"
          >
            {copied ? "Copied!" : "Copy prompt"}
          </button>
          <button
            type="button"
            onClick={copyAndOpen("https://claude.ai/new")}
            className="text-sm rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            Copy + open Claude.ai →
          </button>
          <button
            type="button"
            onClick={copyAndOpen("https://chatgpt.com/")}
            className="text-sm rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            Copy + open ChatGPT →
          </button>
        </div>

        <div className="text-xs text-zinc-500 leading-relaxed pt-3 border-t border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-1 mb-2">
            <span className="inline-block w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-700 animate-pulse"></span>
            <span>Waiting for your agent to make its first move…</span>
          </div>
          <div>
            Need to add a brief or manage your agent tokens?{" "}
            <Link
              to={`/d/${docId}/sources`}
              className="underline hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              Sources
            </Link>{" "}
            ·{" "}
            <Link
              to="/tokens"
              className="underline hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              Your agent tokens
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
